import React, { useMemo, useState, useEffect } from "react";
import { Switch, Route, useRouteMatch } from "react-router-dom";

import { StyledTitle } from "../../styles";
import { Col } from "react-bootstrap";
import { PostRow, PostTeaserTitle } from "./styles";
import Authors from "../../components/Authors";
import BlogPost from "./BlogPost";
import { Button, Stack } from "react-bootstrap";
import { Helmet } from "react-helmet";

const BlogPostTeaser = ({ post, people }) => {
  let postLoc = "/blog/" + post.slug + "/";
  return (
    <PostRow key={post.slug}>
      <Col>
        <PostTeaserTitle to={postLoc}>{post.title}</PostTeaserTitle>
        <div>
          <Authors names={post.authors} people={people} />
        </div>
        <div>{post.date}</div>
      </Col>
    </PostRow>
  );
};

const Blog = ({ posts, people }) => {
  let { path } = useRouteMatch();
  return (
    <Switch>
      <Route exact path={path}>
        <StyledTitle id="blog-posts">Blog</StyledTitle>
        {posts.map((post) => (
          <BlogPostTeaser key={post.slug} post={post} people={people} />
        ))}
      </Route>
      <Route exact path={`${path}/add`}>
        <AddEditBlog posts={posts} />
      </Route>
      <Route path={`${path}/:postId`}>
        <BlogPost posts={posts} people={people} />
      </Route>
    </Switch>
  );
};

export default Blog;

const copyToClipboard = async (text) => {
  await navigator.clipboard.writeText(text);
};

const AddEditBlog = ({ posts = [] }) => {
  const [outputJson, setOutputJson] = useState(null);
  const [agree, setAgree] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedKey, setSelectedKey] = useState(""); // urlId
  const [prefill, setPrefill] = useState(null);
  const [formKey, setFormKey] = useState(0);

  const options = useMemo(() => {
    return (posts || [])
      .map((p) => ({ key: p.urlId, label: `${p.title} — ${p.urlId}`, post: p }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [posts]);

  function switchMode(next) {
    setMode(next);
    if (next === "add") {
      setPrefill(null);
      setSelectedKey("");
      setFormKey((k) => k + 1);
    }
  }

  function handleSelectExisting(e) {
    const key = e.target.value;
    setSelectedKey(key);
    const found = options.find((o) => o.key === key);
    if (!found) {
      setPrefill(null);
      return;
    }
    const p = found.post;
    const base = {
      date: p.date || "",
      urlId: p.urlId || "",
      title: p.title || "",
      authors: (p.authors || []).join(", "),
      previousUrlId: p.urlId || "",
    };
    setPrefill(base);
    setFormKey((k) => k + 1);

    // Try to fetch markdown from public BlogPosts
    const slug = `${p.date || ""}-${p.urlId || ""}`;
    const url = `${process.env.PUBLIC_URL}/BlogPosts/${slug}.md`;
    fetch(url)
      .then((res) => (res.ok ? res.text() : ""))
      .then((text) => {
        setPrefill((prev) => ({ ...(prev || base), markdown: text || "" }));
        setFormKey((k) => k + 1);
      })
      .catch(() => {
        // ignore fetch errors; leave markdown empty
      });
  }

  function handleSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const raw = Object.fromEntries(formData.entries());

    const authors = (raw.authors || "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const post = {
      date: raw.date || "",
      urlId: raw.urlId || "",
      title: raw.title || "",
      authors,
    };
    const payload =
      mode === "edit"
        ? { action: "update", previousUrlId: prefill?.previousUrlId || post.urlId, post, markdown: raw.markdown || "" }
        : { post, markdown: raw.markdown || "" };
    setOutputJson(payload);
  }

  const jsonString = outputJson ? JSON.stringify(outputJson, null, 2) : "";

  return (
    <div>
      <h3>Add or edit a blog post</h3>
      <Helmet>
        <title>Add or edit a blog post - Khoury Vis Lab</title>
        <meta name="description" content="Add or edit a blog post" />
      </Helmet>

      <div style={{ marginBottom: 12 }}>
        <label>
          <input type="radio" name="mode" value="add" checked={mode === "add"} onChange={() => switchMode('add')} />{' '}Add
        </label>{' '}
        <label style={{ marginLeft: 16 }}>
          <input type="radio" name="mode" value="edit" checked={mode === "edit"} onChange={() => switchMode('edit')} />{' '}Edit
        </label>
      </div>

      {mode === "edit" && (
        <div style={{ marginBottom: 12 }}>
          <label>
            <strong>Select existing post</strong>
            <br />
            <select value={selectedKey} onChange={handleSelectExisting}>
              <option value="">-- choose --</option>
              {options.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      <form key={formKey} method="post" onSubmit={handleSubmit}>
        <hr />
        <h4>Required metadata</h4>
        <p>
          <label>
            <strong>Date</strong> — YYYY-MM-DD
            <br />
            <textarea name="date" rows={1} cols={120} defaultValue={prefill?.date || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>URL ID</strong> — e.g., "vis-2025"
            <br />
            <textarea name="urlId" rows={1} cols={120} defaultValue={prefill?.urlId || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>Title</strong>
            <br />
            <textarea name="title" rows={1} cols={120} defaultValue={prefill?.title || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>Authors</strong> — comma-separated names
            <br />
            <textarea name="authors" rows={1} cols={120} defaultValue={prefill?.authors || ''} />
          </label>
        </p>

        <h4>Markdown content</h4>
        <p>
          <label>
            <strong>Content</strong> — Markdown supported
            <br />
            <textarea name="markdown" rows={16} cols={120} defaultValue={prefill?.markdown || ''} />
          </label>
        </p>

        <hr />
        <h4>Submitting</h4>
        <label>
          <input
            type="checkbox"
            defaultChecked={false}
            checked={agree}
            onChange={() => setAgree(!agree)}
          />{' '}
          I have filled required fields and checked for accuracy.
        </label>
        <Button type="submit" className="mt-3 d-block" disabled={!agree}>
          Step 1: Generate JSON
        </Button>
      </form>

      {outputJson && (
        <div>
          <Stack direction="horizontal" gap={3} className="mt-2">
            <Button onClick={() => copyToClipboard(jsonString)}>
              Step 2: Copy to clipboard
            </Button>
            <Button
              className="mt-2"
              onClick={() => {
                const isEdit = mode === "edit";
                const baseUrl = isEdit
                  ? "https://github.com/KhouryVis/khouryvis.github.io/issues/new?assignees=codydunne%2C+makhoroz%2C+airyimbin&labels=update_blog&projects=&title=[UPDATE BLOG] "
                  : "https://github.com/KhouryVis/khouryvis.github.io/issues/new?assignees=codydunne%2C+makhoroz%2C+airyimbin&labels=new_blog&projects=&template=new-blog-template.md&title=[NEW BLOG] ";
                const title = encodeURIComponent(outputJson.post.title || "");
                const url = baseUrl + title;
                window.open(url);
              }}
            >
              Step 3: Paste into new issue
            </Button>
          </Stack>
          <pre>{jsonString}</pre>
        </div>
      )}
    </div>
  );
};
